import { ChangeDetectionStrategy, Component, EventEmitter, Output, input, computed } from '@angular/core';
import {NgClass, TitleCasePipe} from '@angular/common';

@Component({
  selector: 'app-material-color-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, TitleCasePipe],
  styles: [
    `
      @import 'tailwindcss' prefix(ipe);
      @theme {
        --font-display: 'ABC Walter Neue', sans-serif;
        --font-roboto: 'ABC Walter Neue', sans-serif;
        --font-sans: 'ABC Walter Neue', sans-serif;
      }
    `
  ],
  template: `
    @let item = data();
    @let accent = accentFor(item?.color?.hexCodeVesta);

    <button
      type="button"
      class="
        ipe:flex-1
        ipe:w-full
        ipe:cursor-pointer
        ipe:group
        ipe:select-none
        ipe:rounded-xl
        ipe:p-2
        ipe:transition
        ipe:duration-200
        ipe:focus-visible:outline-none
        ipe:focus-visible:ring-1
        ipe:ring-offset-white
      "
      [attr.aria-pressed]="active()"
      [style.--accent]="accent"
      (click)="pressed.emit(item)"
    >
      <span
        class="
          ipe:mx-auto
          ipe:block
          ipe:rounded-full
          ipe:border
          ipe:border-black/10
          ipe:shadow-sm
          ipe:overflow-hidden
          ipe:transition
          ipe:duration-200
          ipe:group-hover:scale-105
        "
        [ngClass]="{
            'ipe:h-6 ipe:w-6': size() === 'xs',
            'ipe:h-8 ipe:w-8': size() === 'sm',
            'ipe:h-10 ipe:w-10': size() === 'md',
            'ipe:h-12 ipe:w-12': size() === 'lg',
            'ipe:h-14 ipe:w-14': size() === 'xl'
          }"
        [ngClass]="classes()"
      >
        @if (item?.color?.imageVestaPath) {
          <img
            [src]="item.color.imageVestaPath"
            class="ipe:h-full ipe:w-full ipe:object-cover"
            loading="lazy"
            decoding="async"
          />
        } @else {
          <span class="ipe:block ipe:h-full ipe:w-full" [style.background]="item?.color?.hexCodeVesta"></span>
        }
      </span>

      @if (displayName()) {
        <span
          class="ipe:mt-2 ipe:block ipe:text-center ipe:text-sm ipe:leading-tight"
          [ngClass]="active() ? 'ipe:text-gray-900 ipe:font-medium' : 'ipe:text-gray-500'"
          title="{{ item?.color?.colorName }} • {{ item?.material?.materialName }}"
        >
        <span class="ipe:block ipe:truncate ipe:text-[11px]">{{ item?.color?.colorName | titlecase}}</span>
<!--        <span class="ipe:block ipe:truncate">{{ item?.material?.materialName }}</span>-->
      </span>
      }
    </button>
  `,
})
export class MaterialColorItemComponent {
  readonly data = input.required<any>();
  readonly active = input(false);
  readonly displayName = input(false);
  readonly size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('xl');
  readonly sizeClass = computed(() => {
    switch (this.size()) {
      case 'xs': return 'ipe:h-6 ipe:w-6';
      case 'sm': return 'ipe:h-8 ipe:w-8';
      case 'md': return 'ipe:h-10 ipe:w-10';
      case 'lg': return 'ipe:h-12 ipe:w-12';
      case 'xl': return 'ipe:h-14 ipe:w-14';
      default:   return 'ipe:h-10 ipe:w-10';
    }
  });
  readonly activeClass = computed(() =>
    this.active()
      ? 'ipe:ring-1 ipe:ring-[var(--accent)] ipe:ring-offset-white'
      : ''
  );
  readonly classes = computed(() => `${this.sizeClass()} ${this.activeClass()}`.trim());

  @Output() pressed = new EventEmitter<any>();

  private readonly accentCache = new Map<string, string>();
  private readonly FALLBACK = '#000';

  accentFor(hex?: string | null): string {
    const raw = (hex ?? '').trim();
    if (!raw) return this.FALLBACK;

    const cached = this.accentCache.get(raw);
    if (cached) return cached;

    let v = raw.toLowerCase();
    if (!v.startsWith('#')) v = `#${v}`;
    if (v.length === 4) v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;

    const accent = v === '#ffffff' ? this.FALLBACK : v;
    this.accentCache.set(raw, accent);
    return accent;
  }
}
